import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { LOGIN_COPY, UserRole, type AuthSession, type AuthUser } from '../constants/auth'
import { ApiError } from '../helpers/api-client'
import { fetchCurrentUser, loginWithPassword, logoutSession } from '../helpers/auth-api'
import {
  clearAuthSession,
  getRefreshToken,
  isVendorSession,
  readAuthSession,
  subscribeAuthSession,
  writeAuthSession,
} from '../helpers/auth-storage'

type AuthContextValue = {
  user: AuthUser | null
  vendorId: string | null
  isAuthenticated: boolean
  isReady: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [session, setSession] = useState<AuthSession | null>(() => {
    const stored = readAuthSession()
    return isVendorSession(stored) ? stored : null
  })
  const [isReady, setIsReady] = useState(() => !readAuthSession())

  useLayoutEffect(() => subscribeAuthSession(setSession), [])

  useEffect(() => {
    const stored = readAuthSession()
    if (!isVendorSession(stored)) {
      if (stored) clearAuthSession()
      setSession(null)
      setIsReady(true)
      return
    }

    let cancelled = false
    void fetchCurrentUser()
      .then((user) => {
        if (cancelled) return
        if (user.role !== UserRole.Vendor || !user.isActive || !user.vendorId) {
          clearAuthSession()
          return
        }
        const current = readAuthSession()
        if (!current) {
          setSession(null)
          return
        }
        writeAuthSession({ ...current, user })
      })
      .catch(() => {
        if (cancelled) return
        const current = readAuthSession()
        if (!isVendorSession(current)) {
          if (current) clearAuthSession()
          setSession(null)
        }
      })
      .finally(() => {
        if (!cancelled) setIsReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const next = await loginWithPassword(email.trim().toLowerCase(), password)
    if (!next.user.isActive) {
      clearAuthSession()
      throw new ApiError(LOGIN_COPY.inactive, 403)
    }
    if (next.user.role !== UserRole.Vendor) {
      clearAuthSession()
      throw new ApiError(LOGIN_COPY.nonVendor, 403)
    }
    if (!next.user.vendorId) {
      clearAuthSession()
      throw new ApiError(LOGIN_COPY.missingVendor, 403)
    }
    queryClient.clear()
    setSession(next)
  }, [queryClient])

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken()
    queryClient.clear()
    await logoutSession(refreshToken)
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      vendorId: session?.user.vendorId ?? null,
      isAuthenticated: isVendorSession(session),
      isReady,
      login,
      logout,
    }),
    [session, isReady, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return value
}
